/**
 * portalTheme — brand-native MUI theme factory for Portal v2 (DEC-063).
 *
 * Generated from output/brand.yaml via the design layer: OKLCH values are
 * preserved as CSS custom properties (injectBrandTokens); the MUI palettes
 * below are codegen-time hex conversions of the same tokens. Dark-mode
 * neutrals are OKLCH-derived from the brand background at generation time.
 *
 * Do NOT hand-edit colors here — change brand.yaml (codaic brand pick) and
 * re-scaffold. (Portal is codegen-exempt: hand-edits survive, but colors
 * belong to the brand pipeline.)
 *
 * Reserved CSS-var namespace for the dashboard pass (P2):
 *   --brand-chart-positive / --brand-chart-negative / --brand-chart-neutral /
 *   --brand-chart-accent  (not emitted yet — see study decision 2, DEC-063).
 */

import { createTheme, type Theme, type PaletteMode } from '@mui/material/styles';
import { densityTokens, type Density } from './AppearanceContext';

// ── Brand tokens (OKLCH-canonical, injected as CSS custom properties) ─────────

export const brandCssVars: Record<string, string> = {
  '--brand-primary': '#1565C0',
  '--brand-secondary': '#546E7A',
  '--brand-error': '#D32F2F',
  '--brand-warning': '#ED6C02',
  '--brand-success': '#2E7D32',
  '--brand-info': '#0288D1',
  '--brand-background': '#FAFAFA',
  '--brand-surface': '#FFFFFF',
  '--brand-chart-positive': '#2E7D32',
  '--brand-chart-negative': '#D32F2F',
  '--brand-chart-neutral': '#546E7A',
  '--brand-chart-series-1': 'oklch(0.513 0.14 255.7)',
  '--brand-chart-series-2': 'oklch(0.513 0.14 300.7)',
  '--brand-chart-series-3': 'oklch(0.513 0.14 345.7)',
  '--brand-chart-series-4': 'oklch(0.513 0.14 55.7)',
  '--brand-chart-series-5': 'oklch(0.513 0.14 105.7)',
  '--brand-chart-series-6': 'oklch(0.513 0.14 150.7)',
  '--brand-chart-series-7': 'oklch(0.513 0.14 195.7)',
  '--brand-chart-series-8': 'oklch(0.513 0.14 225.7)',
};

export function injectBrandTokens(root: HTMLElement = document.documentElement): void {
  for (const [k, v] of Object.entries(brandCssVars)) {
    root.style.setProperty(k, v);
  }
}

// ── Palettes (codegen-time hex conversions of the brand tokens) ───────────────

const lightPalette = {
  mode: 'light' as PaletteMode,
  primary: { main: '#1565C0' },
  secondary: { main: '#546E7A' },
  error: { main: '#d32f2f' },
  warning: { main: '#ed6c02' },
  info: { main: '#0288d1' },
  success: { main: '#2e7d32' },
  background: { default: '#FAFAFA', paper: '#FFFFFF' },
};

const darkPalette = {
  mode: 'dark' as PaletteMode,
  primary: { main: '#5ba6ff' },
  secondary: { main: '#8eaab7' },
  error: { main: '#d32f2f' },
  warning: { main: '#ed6c02' },
  info: { main: '#0288d1' },
  success: { main: '#2e7d32' },
  background: { default: '#0a0a0a', paper: '#141414' },
};

// ── Theme factory ─────────────────────────────────────────────────────────────

export function createPortalTheme(density: Density, mode: 'light' | 'dark'): Theme {
  const d = densityTokens(density);
  return createTheme({
    palette: mode === 'dark' ? darkPalette : lightPalette,
    typography: {
      fontFamily: "Inter, sans-serif",
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: d.cellPaddingY,
            paddingBottom: d.cellPaddingY,
            paddingLeft: d.cellPaddingX,
            paddingRight: d.cellPaddingX,
            fontSize: d.bodyFontSize,
          },
          head: {
            fontSize: d.headerFontSize,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
    },
  });
}
